import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DataTable from './DataTable';
import { useAuth } from '../contexts/AuthContext';
import DepartmentSelect from './DepartmentSelect';
import SearchableSelect from './SearchableSelect';
import ExternalEmployeeSelect from './ExternalEmployeeSelect';
import EmployeeBulkOperations from './EmployeeBulkOperations';
import Alert from './Alert';
import { useAlert } from '../hooks/useAlert';
import { fetchAllPaginatedItems } from '../utils/paginationUtils';

const API_URL = '/api';

const getEmployeeAssignmentId = (employee) => (
  employee?.id ?? employee?.Id ?? employee?.externalEmployeeId ?? employee?.ExternalEmployeeId ?? ''
);


function Employees() {
  const { hasPermission } = useAuth();
  const canEditEmployees = hasPermission('canManageEmployees');
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize] = useState(25);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [formData, setFormData] = useState({
    name: '',
    usl: '',
    mobile: '',
    department: '',
    officeEmployee: false,
    discomName: '',
    zone: '',
    circle: '',
    division: ''
  });
  const [discomNames, setDiscomNames] = useState([]);
  const [zones, setZones] = useState([]);
  const [circles, setCircles] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showAssignmentsModal, setShowAssignmentsModal] = useState(false);
  const [selectedEmployeeAssignments, setSelectedEmployeeAssignments] = useState([]);
  const [empAssignPage, setEmpAssignPage] = useState(1);
  const [empAssignPageSize] = useState(10);
  const [empAssignTotal, setEmpAssignTotal] = useState(0);
  const [viewingEmployee, setViewingEmployee] = useState(null);
  const [empAssignLoading, setEmpAssignLoading] = useState(false);
  const [assetFilter, setAssetFilter] = useState('all');
  const [employeeTypeFilter, setEmployeeTypeFilter] = useState('all');
  const { alert, showAlert, hideAlert } = useAlert();

  useEffect(() => {
    fetchDiscomNames();
    document.title = 'Employees - Saral Asset Manager';
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setTablePage(1);
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    fetchEmployees();
  }, [tablePage, tablePageSize, debouncedSearchTerm, assetFilter, employeeTypeFilter, sortField, sortDirection]);

  const fetchDiscomNames = async () => {
    try {
      const response = await axios.get(`${API_URL}/discoms/discom-names`);
      setDiscomNames(response.data);
    } catch (error) {
      console.error('Error fetching discom names:', error);
    }
  };

  const fetchZones = async (discomName) => {
    try {
      const response = await axios.get(`${API_URL}/discoms/zones/${discomName}`);
      setZones(response.data);
    } catch (error) {
      console.error('Error fetching zones:', error);
    }
  };

  const fetchCircles = async (discomName, zone) => {
    try {
      const response = await axios.get(`${API_URL}/discoms/circles/${discomName}/${zone}`);
      setCircles(response.data);
    } catch (error) {
      console.error('Error fetching circles:', error);
    }
  };

  const fetchDivisions = async (discomName, zone, circle) => {
    try {
      const response = await axios.get(`${API_URL}/discoms/divisions/${discomName}/${zone}/${circle}`);
      setDivisions(response.data);
    } catch (error) {
      console.error('Error fetching divisions:', error);
    }
  };

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/employees/paged`, {
        params: {
          page: tablePage,
          pageSize: tablePageSize,
          search: debouncedSearchTerm || undefined,
          assetFilter,
          employeeType: employeeTypeFilter,
          sortBy: sortField || undefined,
          sortDir: sortField ? sortDirection : undefined
        }
      });

      setEmployees(response.data?.items || []);
      setTotalEmployees(response.data?.total || 0);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]);
      setTotalEmployees(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let selectedDiscom = null;

      if (!formData.officeEmployee) {
        const discomResponse = await axios.get(`${API_URL}/discoms`);
        selectedDiscom = discomResponse.data.find(d => 
          d.discomName === formData.discomName &&
          d.zone === formData.zone &&
          d.circle === formData.circle &&
          d.division === formData.division
        );
      }
      
      const submitData = {
        name: formData.name,
        USL: formData.usl,
        mobile: formData.mobile,
        department: formData.department,
        discomId: selectedDiscom?.id || null
      };
      
      if (editingId) {
        await axios.put(`${API_URL}/employees/${editingId}`, submitData);
        handleCloseModal();
        showAlert('success', 'Employee updated successfully!');
      } else {
        await axios.post(`${API_URL}/employees`, submitData);
        handleCloseModal();
        showAlert('success', 'Employee added successfully!');
      }
      fetchEmployees();
    } catch (error) {
      console.error('Error saving employee:', error);
      const errorMsg = error.response?.data || error.message || 'Failed to save employee';
      showAlert('danger', `Error: ${errorMsg}`);
    }
  };

  const handleAdd = () => {
    setFormData({ name: '', usl: '', mobile: '', department: '', officeEmployee: false, discomName: '', zone: '', circle: '', division: '' });
    setEditingId(null);
    setZones([]);
    setCircles([]);
    setDivisions([]);
    setShowModal(true);
  };

  const handleEdit = (employee) => {
    setFormData({
      name: employee.name,
      usl: employee.usl || '',
      mobile: employee.mobile,
      department: employee.department,
      officeEmployee: !employee.discom,
      discomName: employee.discom?.discomName || '',
      zone: employee.discom?.zone || '',
      circle: employee.discom?.circle || '',
      division: employee.discom?.division || ''
    });
    setEditingId(employee.id);
    
    // Load cascading data if discom is selected
    if (employee.discom?.discomName) {
      fetchZones(employee.discom.discomName);
      if (employee.discom.zone) {
        fetchCircles(employee.discom.discomName, employee.discom.zone);
        if (employee.discom.circle) {
          fetchDivisions(employee.discom.discomName, employee.discom.zone, employee.discom.circle);
        }
      }
    }
    
    setShowModal(true);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API_URL}/employees/${deleteId}`);
      setShowDeleteModal(false);
      setDeleteId(null);
      showAlert('success', 'Employee deleted successfully!');
      fetchEmployees();
    } catch (error) {
      console.error('Error deleting employee:', error);
      const errorMsg = error.response?.data || error.message || 'Failed to delete employee';
      showAlert('danger', `Error: ${errorMsg}`);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({ name: '', usl: '', mobile: '', department: '', officeEmployee: false, discomName: '', zone: '', circle: '', division: '' });
    setEditingId(null);
    setZones([]);
    setCircles([]);
    setDivisions([]);
  };

  const handleDiscomChange = (value) => {
    const discomName = value?.target ? value.target.value : value;
    setFormData({ ...formData, discomName, zone: '', circle: '', division: '' });
    setZones([]);
    setCircles([]);
    setDivisions([]);
    if (discomName) {
      fetchZones(discomName);
    }
  };

  const handleOfficeEmployeeChange = (checked) => {
    setFormData({
      ...formData,
      officeEmployee: checked,
      discomName: checked ? '' : formData.discomName,
      zone: checked ? '' : formData.zone,
      circle: checked ? '' : formData.circle,
      division: checked ? '' : formData.division
    });

    if (checked) {
      setZones([]);
      setCircles([]);
      setDivisions([]);
    }
  };

  const handleZoneChange = (value) => {
    const zone = value?.target ? value.target.value : value;
    setFormData({ ...formData, zone, circle: '', division: '' });
    setCircles([]);
    setDivisions([]);
    if (zone && formData.discomName) {
      fetchCircles(formData.discomName, zone);
    }
  };

  const handleCircleChange = (value) => {
    const circle = value?.target ? value.target.value : value;
    setFormData({ ...formData, circle, division: '' });
    setDivisions([]);
    if (circle && formData.discomName && formData.zone) {
      fetchDivisions(formData.discomName, formData.zone, circle);
    }
  };

  const fetchEmpAssignmentsPage = async (employee, page) => {
    const employeeAssignmentId = getEmployeeAssignmentId(employee);
    setEmpAssignLoading(true);
    try {
      const response = await axios.get(`${API_URL}/assetassignments/paged`, {
        params: {
          page,
          pageSize: empAssignPageSize,
          employeeId: employeeAssignmentId,
          status: 'active',
          sortBy: 'assignedDate',
          sortDir: 'desc'
        }
      });
      setSelectedEmployeeAssignments(response.data?.items || []);
      setEmpAssignTotal(response.data?.total || 0);
      setEmpAssignPage(page);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      setSelectedEmployeeAssignments([]);
      setEmpAssignTotal(0);
    } finally {
      setEmpAssignLoading(false);
    }
  };

  const handleViewAssignments = async (employee) => {
    setViewingEmployee(employee);
    setShowAssignmentsModal(true);
    await fetchEmpAssignmentsPage(employee, 1);
  };

  const assetFilterOptions = [
    { value: 'all', label: 'All Employees' },
    { value: 'with-assets', label: 'With Assets' },
    { value: 'without-assets', label: 'Without Assets' }
  ];

  const employeeTypeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'office', label: 'Office' },
    { value: 'discom', label: 'Discom' }
  ];

  const hasActiveQuickFilters = assetFilter !== 'all' || employeeTypeFilter !== 'all';

  const quickFilterToolbar = (
    <div className="d-flex flex-wrap flex-md-nowrap align-items-center gap-2 h-100 w-100 grid-filter-toolbar">
      <SearchableSelect
        className="flex-grow-1 grid-filter-select grid-filter-select-wide"
        controlClassName="form-control-sm"
        options={assetFilterOptions}
        value={assetFilter}
        placeholder="All Employees"
        onChange={(e) => {
          setAssetFilter(e.target.value);
          setTablePage(1);
        }}
      />
      <SearchableSelect
        className="flex-grow-1 grid-filter-select"
        controlClassName="form-control-sm"
        options={employeeTypeOptions}
        value={employeeTypeFilter}
        placeholder="All Types"
        onChange={(e) => {
          setEmployeeTypeFilter(e.target.value);
          setTablePage(1);
        }}
      />
      <button
        className="btn btn-sm btn-outline-secondary grid-filter-clear"
        onClick={() => {
          setAssetFilter('all');
          setEmployeeTypeFilter('all');
          setTablePage(1);
        }}
        disabled={!hasActiveQuickFilters}
      >
        <i className="fas fa-eraser me-1"></i>Clear
      </button>
    </div>
  );

  const columns = [
    { field: 'id', label: 'ID' },
    { field: 'name', label: 'Name' },
    { field: 'usl', label: 'USL' },
    { field: 'mobile', label: 'Mobile' },
    { field: 'department', label: 'Department' },
    { 
      field: 'assetCount', 
      label: 'Assets',
      sortField: 'assetCount',
      exportValue: (value) => value || 0,
      render: (value, item) => (
        value > 0 ? (
          <button
            type="button"
            className="btn btn-link p-0 border-0 text-decoration-none"
            onClick={() => handleViewAssignments(item)}
            title="View assigned assets"
          >
            <span className="badge bg-success">
              {value}
            </span>
          </button>
        ) : (
          <span className="badge bg-secondary">
            {value || 0}
          </span>
        )
      )
    },
    { 
      field: 'isExternal', 
      label: 'Type',
      sortField: 'isExternal',
      exportValue: (value) => value ? 'External' : 'Local',
      render: (value) => (
        <span className={`badge ${value ? 'bg-info' : 'bg-secondary'}`}>
          {value ? 'External' : 'Local'}
        </span>
      )
    },
    { 
      field: 'discom', 
      label: 'Discom',
      sortField: 'discomName',
      exportValue: (value) => value?.discomName || 'N/A',
      render: (value) => value?.discomName || 'N/A'
    },
    { 
      field: 'discom', 
      label: 'Zone',
      sortField: 'zone',
      exportValue: (value) => value?.zone || 'N/A',
      render: (value) => value?.zone || 'N/A'
    },
    { 
      field: 'discom', 
      label: 'Circle',
      sortField: 'circle',
      exportValue: (value) => value?.circle || 'N/A',
      render: (value) => value?.circle || 'N/A'
    },
    { 
      field: 'discom', 
      label: 'Division',
      sortField: 'division',
      exportValue: (value) => value?.division || 'N/A',
      render: (value) => value?.division || 'N/A'
    },
    { 
      field: 'createdAt', 
      label: 'Created At',
      sortField: 'createdAt',
      exportValue: (value) => new Date(value).toLocaleDateString(),
      render: (value) => new Date(value).toLocaleDateString(),
      sortValue: (value) => new Date(value).getTime()
    }
  ];

  return (
    <div className="container-fluid">
      <Alert show={alert.show} type={alert.type} message={alert.message} onClose={hideAlert} />
      
      <div className="page-header">
        <h1 className="page-title mb-0">Employees</h1>
        <div className="page-header-actions">
          {canEditEmployees && (
            <>
              <button className="btn btn-outline-info btn-sm" onClick={() => setShowBulkModal(true)}>
                <i className="fas fa-upload me-1"></i><span className="d-none d-sm-inline">Bulk Operations</span><span className="d-sm-none">Bulk</span>
              </button>
              <button className="btn btn-primary btn-sm" onClick={handleAdd}>
                <i className="fas fa-plus me-1"></i><span className="d-none d-sm-inline">Add Employee</span><span className="d-sm-none">Add</span>
              </button>
            </>
          )}
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <DataTable
            data={employees}
            columns={columns}
            toolbarContent={quickFilterToolbar}
            serverSide
            loading={loading}
            page={tablePage}
            pageSize={tablePageSize}
            totalItems={totalEmployees}
            searchValue={searchTerm}
            onSearchChange={setSearchTerm}
            sortField={sortField}
            sortDirection={sortDirection}
            onSortChange={(field, direction) => {
              setSortField(field);
              setSortDirection(direction);
              setTablePage(1);
            }}
            onPageChange={setTablePage}
            getExportData={() => fetchAllPaginatedItems(async (page, pageSize) => {
              const response = await axios.get(`${API_URL}/employees/paged`, {
                params: {
                  page,
                  pageSize,
                  search: debouncedSearchTerm || undefined,
                  assetFilter,
                  employeeType: employeeTypeFilter,
                  sortBy: sortField || undefined,
                  sortDir: sortField ? sortDirection : undefined
                }
              });

              return response.data;
            })}
            exportFileName="employees"
            onEdit={canEditEmployees ? handleEdit : undefined}
            onDelete={canEditEmployees ? (id) => {
              if (typeof id === 'number' || !id.toString().startsWith('ext_')) {
                handleDelete(id);
              }
            } : undefined}
            customActions={(item) => (
              <li>
                <button className="dropdown-item" onClick={() => handleViewAssignments(item)}>
                  <i className="fas fa-box me-2"></i>View Assets ({item.assetCount || 0})
                </button>
              </li>
            )}
            hideActions={(item) => item.isExternal}
          />
        </div>
      </div>

      {/* Modal */}
      <div className={`modal fade ${showModal ? 'show d-block' : ''}`} style={{backgroundColor: showModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-users me-2"></i>
                {editingId ? 'Edit Employee' : 'Add New Employee'}
              </h5>
              <button type="button" className="btn-close" onClick={handleCloseModal}></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter employee name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">USL</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter USL"
                    value={formData.usl}
                    onChange={(e) => setFormData({ ...formData, usl: e.target.value })}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Mobile Number</label>
                  <input
                    type="tel"
                    className="form-control"
                    placeholder="Enter mobile number"
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                    pattern="[0-9]{10}"
                    title="Please enter a valid 10-digit mobile number"
                    required
                  />
                  <small className="text-muted">Enter 10-digit mobile number</small>
                </div>
                <div className="mb-3">
                  <label className="form-label">Department</label>
                  <DepartmentSelect
                    value={formData.department}
                    onChange={(value) => {
                      const actualValue = value?.target ? value.target.value : value;
                      setFormData({ ...formData, department: actualValue });
                    }}
                    required
                  />
                </div>

                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="officeEmployee"
                    checked={formData.officeEmployee}
                    onChange={(e) => handleOfficeEmployeeChange(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="officeEmployee">
                    Office Employee
                  </label>
                </div>

                {!formData.officeEmployee && (
                  <>
                    <div className="mb-3">
                      <label className="form-label">Discom <span className="text-muted">(Optional)</span></label>
                      <SearchableSelect
                        options={discomNames.map(name => ({ value: name, label: name }))}
                        value={formData.discomName}
                        onChange={(value) => handleDiscomChange(value)}
                        placeholder="Select Discom"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Zone <span className="text-muted">(Optional)</span></label>
                      <SearchableSelect
                        options={zones.map(zone => ({ value: zone, label: zone }))}
                        value={formData.zone}
                        onChange={(value) => handleZoneChange(value)}
                        placeholder="Select Zone"
                        disabled={!formData.discomName}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Circle <span className="text-muted">(Optional)</span></label>
                      <SearchableSelect
                        options={circles.map(circle => ({ value: circle, label: circle }))}
                        value={formData.circle}
                        onChange={(value) => handleCircleChange(value)}
                        placeholder="Select Circle"
                        disabled={!formData.zone}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Division <span className="text-muted">(Optional)</span></label>
                      <SearchableSelect
                        options={divisions.map(division => ({ value: division, label: division }))}
                        value={formData.division}
                        onChange={(value) => {
                          const actualValue = value?.target ? value.target.value : value;
                          setFormData({ ...formData, division: actualValue });
                        }}
                        placeholder="Select Division"
                        disabled={!formData.circle}
                      />
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  <i className="fas fa-save me-1"></i>
                  {editingId ? 'Update Employee' : 'Add Employee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <div className={`modal fade ${showDeleteModal ? 'show d-block' : ''}`} style={{backgroundColor: showDeleteModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-exclamation-triangle me-2 text-warning"></i>
                Confirm Delete
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
            </div>
            <div className="modal-body">
              Are you sure you want to delete this employee? This action cannot be undone.
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={confirmDelete}>
                <i className="fas fa-trash me-1"></i>
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Operations Modal */}
      <EmployeeBulkOperations 
        show={showBulkModal} 
        onHide={() => setShowBulkModal(false)}
        onRefresh={fetchEmployees}
      />

      <div className={`modal fade ${showAssignmentsModal ? 'show d-block' : ''}`} style={{backgroundColor: showAssignmentsModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title"><i className="fas fa-box me-2"></i>Assigned Assets</h5>
              <button type="button" className="btn-close" onClick={() => setShowAssignmentsModal(false)}></button>
            </div>
            <div className="modal-body">
              {empAssignLoading ? (
                <div className="d-flex justify-content-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : selectedEmployeeAssignments.length > 0 ? (
                <>
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Saral Asset ID</th>
                          <th>Asset</th>
                          <th>Serial Number</th>
                          <th>Assigned Date</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedEmployeeAssignments.map((assignment) => (
                          <tr key={assignment.id}>
                            <td>{assignment.saralAssetId}</td>
                            <td>{assignment.asset?.name || 'N/A'}</td>
                            <td>{assignment.serialNumber}</td>
                            <td>{new Date(assignment.assignedDate).toLocaleDateString()}</td>
                            <td>
                              <span className={`badge ${assignment.deliveryStatus === 2 ? 'bg-success' : assignment.deliveryStatus === 1 ? 'bg-info' : 'bg-warning'}`}>
                                {assignment.deliveryStatus === 2 ? 'Delivered' : assignment.deliveryStatus === 1 ? 'In Transit' : 'Pending'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {(empAssignTotal > empAssignPageSize) && (
                    <div className="table-footer-bar">
                      <nav className="mb-0">
                        <ul className="pagination justify-content-center mb-0">
                          <li className={`page-item ${empAssignPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => fetchEmpAssignmentsPage(viewingEmployee, 1)} disabled={empAssignPage === 1}>
                              <i className="fas fa-angle-double-left"></i>
                            </button>
                          </li>
                          <li className={`page-item ${empAssignPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => fetchEmpAssignmentsPage(viewingEmployee, empAssignPage - 1)} disabled={empAssignPage === 1}>
                              <i className="fas fa-angle-left"></i>
                            </button>
                          </li>
                          {(() => {
                            const totalPages = Math.ceil(empAssignTotal / empAssignPageSize);
                            const maxVisible = 5;
                            const halfVisible = Math.floor(maxVisible / 2);
                            let startPage = Math.max(1, empAssignPage - halfVisible);
                            let endPage = Math.min(totalPages, startPage + maxVisible - 1);
                            if (endPage - startPage < maxVisible - 1) {
                              startPage = Math.max(1, endPage - maxVisible + 1);
                            }
                            const pages = [];
                            for (let i = startPage; i <= endPage; i++) {
                              pages.push(
                                <li key={i} className={`page-item ${empAssignPage === i ? 'active' : ''}`}>
                                  <button className="page-link" onClick={() => fetchEmpAssignmentsPage(viewingEmployee, i)}>{i}</button>
                                </li>
                              );
                            }
                            return pages;
                          })()}
                          <li className={`page-item ${empAssignPage >= Math.ceil(empAssignTotal / empAssignPageSize) ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => fetchEmpAssignmentsPage(viewingEmployee, empAssignPage + 1)} disabled={empAssignPage >= Math.ceil(empAssignTotal / empAssignPageSize)}>
                              <i className="fas fa-angle-right"></i>
                            </button>
                          </li>
                          <li className={`page-item ${empAssignPage >= Math.ceil(empAssignTotal / empAssignPageSize) ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => fetchEmpAssignmentsPage(viewingEmployee, Math.ceil(empAssignTotal / empAssignPageSize))} disabled={empAssignPage >= Math.ceil(empAssignTotal / empAssignPageSize)}>
                              <i className="fas fa-angle-double-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                      <div className="table-entry-count">
                        <i className="fas fa-list me-1"></i>
                        {selectedEmployeeAssignments.length} of {empAssignTotal} entries
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-4">
                  <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                  <p className="text-muted">No active assets assigned to this employee.</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowAssignmentsModal(false)}>Close</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Employees;
